import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Flags
let isRefreshing = false;
let isLoggingOut = false;  // 🚀 new flag
let failedQueue = [];

const createAuthRedirectError = (message = 'Authentication session expired. Redirecting to login.') => {
  const error = new Error(message);
  error.name = 'AuthRedirectError';
  error.isAuthRedirect = true;
  return error;
};

// Process queue of failed requests
const processQueue = (error, token = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  }
  failedQueue = [];
};

// Make sure a CSRF token is available (fetching and persisting one when the
// API's own cookie was rejected cross-host) and return it. Exported so pages
// that must use a bare request can still satisfy Django's CSRF check.
export const ensureCsrfToken = async () => {
    let csrfToken = Cookies.get('csrftoken');
    // If no CSRF token, fetch it before continuing
    if (!csrfToken) {
      try {
        console.log('[CSRF] Missing CSRF token. Fetching new one...');

        // Use a plain axios (not the same instance, to avoid recursion)
        const csrfResponse = await axios.get(`${API_URL}csrf-token/`, {
          withCredentials: true,
        });

        // Get it again from cookies after setting
        csrfToken = Cookies.get('csrftoken');

        // The endpoint scopes its Set-Cookie to the requesting page's host.
        // When the app host is not a parent of the API host (production
        // dms.whgdocs.com vs api.stgdms.whgdocs.com) the browser rejects
        // that cookie and every unsafe request 403s. The endpoint also
        // returns the token in the body: persist it ourselves on the
        // shared registrable domain, which is valid for both hosts.
        const bodyToken = csrfResponse?.data?.token;
        if (!csrfToken && bodyToken) {
          const host = globalThis.window?.location?.hostname || '';
          const cookieOptions = host.endsWith('whgdocs.com')
            ? { domain: '.whgdocs.com', secure: true, expires: 7 }
            : { expires: 7 };
          Cookies.set('csrftoken', bodyToken, cookieOptions);
          csrfToken = bodyToken;
          console.log('[CSRF] Cookie was rejected cross-host; persisted body token on shared domain');
        }
      } catch (err) {
        console.error('[CSRF] Failed to fetch CSRF token:', err);
        throw err;
      }
    }
    return csrfToken;
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    const csrfToken = await ensureCsrfToken();
    // Attach CSRF token to headers
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => {
    throw error instanceof Error ? error : new Error(String(error));
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If a logout redirect is already in flight, fail fast so callers can
    // clear loading state instead of hanging on a never-resolving Promise.
    if (isLoggingOut) {
      return Promise.reject(createAuthRedirectError());
    }

    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    // If already refreshing, queue request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          return api(originalRequest);
        })
        .catch((err) => {
          throw err instanceof Error ? err : new Error(String(err));
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshAxios = axios.create({
        baseURL: API_URL,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const cookieOptions = { path: '/', domain: globalThis.location.hostname };
      Cookies.remove('access_token', cookieOptions);

      const refreshToken = Cookies.get('refresh_token');
      const response = await refreshAxios.post('auth/token/refresh/', {
        refresh_token: refreshToken,
      });

      const { token } = response.data;

      // Retry queued requests
      processQueue(null, token);

      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      // 🚀 Mark as logging out
      isLoggingOut = true;

      // Clear tokens and redirect
      Cookies.remove('access_token', { path: '/', domain: globalThis.location.hostname });
      Cookies.remove('refresh_token', { path: '/', domain: globalThis.location.hostname });
      localStorage.removeItem('user_id');
      globalThis.location.href = '/';

      // Reject so callers can unwind loading state while redirect proceeds.
      return Promise.reject(
        createAuthRedirectError(
          refreshError instanceof Error ? refreshError.message : undefined
        )
      );
    } finally {
      isRefreshing = false;
    }
  }
);

// Fetch current user info
export const fetchCurrentUser = async () => {
  const response = await api.get('/auth/me/');
  return response.data;
};

// Log the current user out. Backend clears auth cookies; caller is responsible
// for clearing client-side state and redirecting.
//
// Uses a bare axios request (NOT the shared `api` instance) on purpose. The
// shared instance's 401 interceptor returns a never-settling promise after a
// failed token refresh, which is intentional for most calls (lets the
// interceptor's own redirect take over), but would make the logout thunk
// silently hang when the session is already expired — `await logoutUser()`
// would never complete, our own cookie cleanup would never run, and
// `logout.fulfilled` would never fire. A plain request short-circuits that
// path so the thunk always finishes, even for expired sessions.
//
// Still attaches the CSRF token manually because Django expects it on POST.
// A 6s timeout keeps a misbehaving server from stranding the UI; any error
// (including timeout / 401 / network) is intentionally swallowed by the
// caller, since the local cookie/state cleanup must run regardless.
export const logoutUser = async () => {
  const csrfToken = Cookies.get('csrftoken');
  const response = await axios.post(
    `${API_URL}auth/logout/`,
    {},
    {
      withCredentials: true,
      timeout: 6000,
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
    }
  );
  return response.data;
};

export default api; 